/**
 * 04/11/2026 (nuit) — Organisme → « Qualiopi & vitrine » (écarts E7, E8, E11, E12).
 * Certificat Qualiopi complet (numéro, certificateur, catégories d'actions, dates, PDF facultatif) avec alerte
 * d'échéance, et textes publics de la vitrine (délais, accessibilité, contact). La vitrine les lit par
 * `/api/public/organisme/infos` ; un champ vide laisse le texte type de la vitrine (ou « À compléter »).
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, FileText, Trash2 } from "lucide-react";
import { ALERTE_ECHEANCE_JOURS, CATEGORIES_QUALIOPI, etatCertificat } from "@/domaine/qualite/certificat-qualiopi";
import { LIBELLES_MODES } from "@/domaine/financements/catalogue";
import { api, dateFr, ErreurApi, type InfosPubliques, type Organisme } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, DepotFichier, useNotifier, ZoneTexte } from "../ui/base";

type Reponse = { organisme: Organisme; manques: string[] };
type Cle =
  | "of_qualiopi_numero"
  | "of_qualiopi_certificateur"
  | "of_qualiopi_date_emission"
  | "of_qualiopi_date_fin"
  | "of_vitrine_delai_reponse"
  | "of_vitrine_delai_acces"
  | "of_vitrine_accessibilite"
  | "of_vitrine_email_contact";
type Modifs = Partial<Record<Cle, string>> & { of_qualiopi_categories?: string[] };

const A_COMPLETER = "À compléter";

export function AlerteCertificat({ etat, jours, dateFin }: { etat: string; jours: number | null; dateFin: string }) {
  if (etat === "echu")
    return (
      <Alerte ton="danger" titre="Certificat Qualiopi échu">
        La fin de validité saisie ({dateFr(dateFin)}) est dépassée : la vitrine n'affiche plus la mention près du logo. Saisissez le nouveau certificat dès sa réception.
      </Alerte>
    );
  if (etat === "a_renouveler")
    return (
      <Alerte ton="attention" titre={`Certificat Qualiopi : échéance dans ${jours} jour(s)`}>
        Fin de validité le {dateFr(dateFin)}. Planifiez l'audit de renouvellement avec votre certificateur (alerte {ALERTE_ECHEANCE_JOURS} jours avant).
      </Alerte>
    );
  if (etat === "non_saisi")
    return (
      <Alerte ton="attention" titre="Certificat Qualiopi incomplet">
        Saisissez au moins la date de fin de validité et les catégories d'actions : tant qu'elles manquent, la vitrine affiche « {A_COMPLETER} ».
      </Alerte>
    );
  return null;
}

const etatEtJours = (dateFin: string) => {
  const e = etatCertificat(dateFin, new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" }));
  return { etat: e.etat, jours: e.jours_restants };
};

export function OngletQualiopiVitrine() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const config = useQuery({ queryKey: ["organisme"], queryFn: () => api.get<Reponse>("/admin/organisme") });
  const apercu = useQuery({ queryKey: ["organisme", "infos-publiques"], queryFn: () => api.get<InfosPubliques>("/admin/organisme/infos-publiques") });
  const [v, setV] = useState<Modifs>({});
  const apres = async (r: Reponse) => {
    requetes.setQueryData(["organisme"], r);
    await requetes.invalidateQueries({ queryKey: ["organisme", "infos-publiques"] });
  };
  const enregistrer = useMutation({
    mutationFn: () => api.patch<Reponse>("/admin/organisme", v),
    onSuccess: async (r) => {
      await apres(r);
      setV({});
      notifier("succes", "Certificat et textes de la vitrine enregistrés.");
    },
  });
  const deposer = useMutation({ mutationFn: (f: File) => api.fichier<Reponse>("/admin/organisme/certificat-qualiopi", f), onSuccess: apres });
  const retirer = useMutation({ mutationFn: () => api.suppr<Reponse>("/admin/organisme/certificat-qualiopi"), onSuccess: apres });

  if (config.isPending) return <Chargement />;
  if (config.error) return <Alerte ton="danger">{config.error.message}</Alerte>;
  const of = { ...config.data.organisme, ...v };
  const err = enregistrer.error instanceof ErreurApi ? (enregistrer.error.details?.champs ?? {}) : {};
  const champ = (cle: Cle, libelle: string, aide?: string, type = "text") => (
    <Champ key={cle} type={type} libelle={libelle} aide={aide} erreur={err[cle]} value={String(of[cle] ?? "")} onChange={(e) => setV({ ...v, [cle]: e.target.value })} />
  );
  const categories = (of.of_qualiopi_categories as string[] | undefined) ?? [];
  const basculer = (code: string) => setV({ ...v, of_qualiopi_categories: categories.includes(code) ? categories.filter((c) => c !== code) : [...categories, code] });
  const i = apercu.data;

  return (
    <form
      className="mt-6 space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        enregistrer.mutate();
      }}
    >
      <AlerteCertificat {...etatEtJours(config.data.organisme.of_qualiopi_date_fin)} dateFin={config.data.organisme.of_qualiopi_date_fin} />
      {enregistrer.error && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}

      <Carte className="p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <BadgeCheck className="size-4 text-encre-3" aria-hidden /> Certificat Qualiopi
        </h2>
        <p className="mt-0.5 text-[13px] text-encre-3">
          Recopiez le certificat tel qu'il vous a été délivré. Rien n'est complété automatiquement : un champ vide reste « {A_COMPLETER} » sur la vitrine. Le numéro de déclaration d'activité et la région se saisissent dans l'onglet « Identité ».
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {champ("of_qualiopi_numero", "N° du certificat")}
          {champ("of_qualiopi_certificateur", "Organisme certificateur", "Nom tel qu'il figure sur le certificat.")}
          {champ("of_qualiopi_date_emission", "Date d'émission", undefined, "date")}
          {champ("of_qualiopi_date_fin", "Fin de validité", `Une alerte apparaît ${ALERTE_ECHEANCE_JOURS} jours avant, ici et dans le pilotage.`, "date")}
        </div>
        <fieldset className="mt-4">
          <legend className="text-sm font-medium">Catégories d'actions certifiées</legend>
          <p className="text-[13px] text-encre-3">Elles composent la mention obligatoire placée près du logo Qualiopi (règlement d'usage de la marque).</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {CATEGORIES_QUALIOPI.map((c) => (
              <label key={c.code} className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-accent)]" checked={categories.includes(c.code)} onChange={() => basculer(c.code)} />
                {c.libelle}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-trait pt-4">
          <FileText className="size-4 text-encre-3" aria-hidden />
          {config.data.organisme.of_qualiopi_pdf_chemin ? (
            <>
              <a className="text-sm font-medium text-accent underline" href="/api/admin/organisme/certificat-qualiopi" target="_blank" rel="noreferrer">
                Voir le certificat déposé (PDF)
              </a>
              <Bouton variante="discret" taille="sm" onClick={() => retirer.mutate()} disabled={retirer.isPending}>
                <Trash2 className="size-3.5" aria-hidden /> Retirer
              </Bouton>
            </>
          ) : (
            <span className="text-sm text-encre-2">Certificat PDF (facultatif) : il sera proposé en téléchargement sur la vitrine.</span>
          )}
          <DepotFichier compact libelle={config.data.organisme.of_qualiopi_pdf_chemin ? "Remplacer le PDF" : "Déposer le PDF"} accept="application/pdf" enCours={deposer.isPending} deposer={(f) => deposer.mutate(f)} />
        </div>
        {deposer.error && <p className="mt-2 text-[13px] text-danger">{deposer.error.message}</p>}
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">Textes publics des fiches formation</h2>
        <p className="mt-0.5 text-[13px] text-encre-3">
          Repris sur chaque fiche de la vitrine (indicateurs 1 et 26). Laissez vide pour garder le texte type actuel de la vitrine. Le référent handicap (nom, e-mail) vient de l'onglet « Identité » ; son téléphone n'est pas publié.
        </p>
        <div className="mt-4 grid gap-4">
          {champ("of_vitrine_email_contact", "E-mail de contact public", "Ex. l'adresse générique de l'organisme.", "email")}
          {champ("of_vitrine_delai_reponse", "Délai de réponse", "Ex. « sous 48 heures ouvrées ».")}
          <ZoneTexte libelle="Délais d'accès" aide="Premier échange, entrée en formation, délai propre au CPF le cas échéant." erreur={err.of_vitrine_delai_acces} value={of.of_vitrine_delai_acces ?? ""} onChange={(e) => setV({ ...v, of_vitrine_delai_acces: e.target.value })} />
          <ZoneTexte libelle="Accessibilité aux personnes en situation de handicap" aide="Doit citer le référent handicap ; sans téléphone." erreur={err.of_vitrine_accessibilite} value={of.of_vitrine_accessibilite ?? ""} onChange={(e) => setV({ ...v, of_vitrine_accessibilite: e.target.value })} />
        </div>
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">Aperçu de ce que lit la vitrine</h2>
        {apercu.isPending ? (
          <Chargement />
        ) : i ? (
          <dl className="mt-3 space-y-3 text-sm">
            <div>
              <dt className="font-medium">Déclaration d'activité</dt>
              <dd className="text-encre-2">{i.declaration_activite.mention ?? A_COMPLETER}</dd>
            </div>
            <div>
              <dt className="font-medium">Mention près du logo Qualiopi</dt>
              <dd className="text-encre-2">{i.qualiopi.mention_logo ?? A_COMPLETER}</dd>
            </div>
            <div>
              <dt className="font-medium">Financements affichés sur les fiches</dt>
              <dd className="text-encre-2">
                {i.financeurs.length ? i.financeurs.map((f) => LIBELLES_MODES[f]).join(", ") : "Aucun financeur activé"} — le CPF seulement sur les fiches dont la certification est enregistrée.{" "}
                <Link to="/admin/financeurs" className="text-accent underline">
                  Activer ou désactiver un financeur
                </Link>
              </dd>
            </div>
          </dl>
        ) : null}
      </Carte>

      <div className="flex justify-end">
        <Bouton type="submit" variante="primaire" enCours={enregistrer.isPending} disabled={Object.keys(v).length === 0}>
          Enregistrer
        </Bouton>
      </div>
    </form>
  );
}
